/**
 * Keyboard map (UX 7.7). Attached by the game screen; ignores keystrokes aimed at form controls so
 * the setup and settings inputs keep working.
 */
import { useEffect } from 'react';
import { useGame } from '../../store/gameStore';
import { useSettings } from '../../store/settings';
import { RING_KEYS } from './labels';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || target.isContentEditable;
}

/** Resolve a ring key (1–9, 0, Q W E R T Y) to a location id on the board. */
export function locationForKey(key: string, locationAt: (string | null)[]): string | null {
  const index = RING_KEYS.indexOf(key.toUpperCase() as (typeof RING_KEYS)[number]);
  if (index < 0) return null;
  return locationAt[index] ?? null;
}

/** Handle one keydown against the current store state; exported for unit tests. */
export function handleGameKey(e: KeyboardEvent): void {
  if (isTypingTarget(e.target)) return;
  const store = useGame.getState();
  const { state, pack } = store;
  if (!state || !pack) return;

  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
    e.preventDefault();
    store.go('saves');
    return;
  }
  const yours = state.players[state.activeSeat]?.controller === 'human-local';
  if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    if (yours) store.togglePalette();
    return;
  }
  if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'z') {
    e.preventDefault();
    if (yours && store.cards.length === 0 && !useSettings.getState().settings.strictMode)
      store.undoLast();
    return;
  }
  if (e.ctrlKey || e.metaKey || e.altKey) return;

  // A retention offer is modal: only its own buttons and Escape may act while it is open.
  if (store.confirmPending) {
    if (e.key === 'Escape') {
      e.preventDefault();
      store.cancelConfirm();
    }
    return;
  }
  if (store.subscriptionCancelPending) {
    if (e.key === 'Escape') {
      e.preventDefault();
      store.cancelSubscriptionCancel();
    }
    return;
  }

  if (e.key === 'Escape') {
    if (store.paletteOpen) store.togglePalette(false);
    else if (store.cards.length > 0) store.dismissCard();
    else if (store.travelOpen) store.closeTravel();
    else if (store.endTurnPending) store.cancelEndTurn();
    else if (store.menuOpen) store.toggleMenu();
    else if (store.standingsOpen) store.toggleStandings();
    else if (store.logOpen) store.toggleLog();
    return;
  }
  // View toggles work any time; anything that could dispatch is limited to the human's own turn.
  const yourTurn = state.players[state.activeSeat]?.controller === 'human-local';

  if (e.key === 'Enter' || e.key === ' ') {
    if (!yourTurn) return;
    if (store.cards.length > 0) {
      e.preventDefault();
      store.dismissCard();
      return;
    }
    if (store.travelOpen && store.selectedLocation !== null) {
      e.preventDefault();
      store.visitLocation(store.selectedLocation, store.travelMode);
    }
    return;
  }
  if (store.cards.length > 0) return;
  // A pop-up owns the keyboard until it is closed (M13.10).
  if (store.info !== null || store.outcomes.length > 0) return;

  if (e.shiftKey && e.key.toLowerCase() === 'e') {
    if (yourTurn) store.requestEndTurn();
    return;
  }
  if (e.shiftKey) {
    const planKey = e.code.startsWith('Digit') ? e.code.slice(5) : e.key;
    const plan = locationForKey(planKey, pack.board.locationAt);
    if (yourTurn && plan !== null && plan !== state.players[state.activeSeat]?.location) {
      e.preventDefault();
      store.openTravel(plan);
    }
    return;
  }

  switch (e.key.toLowerCase()) {
    case 'm':
      if (store.travelOpen) store.cycleTravelMode();
      return;
    case 'l':
      store.toggleLog();
      return;
    case 'g':
      store.toggleStandings();
      return;
    case 'h':
      store.go('help');
      return;
    case 'j':
      store.openInfo({ kind: 'job' });
      return;
    case 'n':
      store.openInfo({ kind: 'home' });
      return;
    case 's':
      store.openInfo({ kind: 'education' });
      return;
    case '/':
      e.preventDefault();
      if (yourTurn) store.togglePalette(true);
      return;
    case 'z':
      if (yourTurn && !useSettings.getState().settings.strictMode) store.undoLast();
      return;
    default:
      break;
  }
  if (!yourTurn) return;
  const loc = locationForKey(e.key, pack.board.locationAt);
  if (loc === null) return;
  e.preventDefault();
  store.visitLocation(loc);
}

export function useKeyboard(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent): void => {
      handleGameKey(e);
    };
    globalThis.addEventListener('keydown', onKey);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
    };
  }, [enabled]);
}
